import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown,
  ChevronRight, Clock, X,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  getSombraCenace,
  getSombraCenaceDetalle,
  type SombraCenaceItem,
} from "@/lib/api";

/*
 * Sombra CENACE: concilia cada TBFin enviada (las del /calendario) contra lo
 * que CENACE liquidó en el estado de cuenta (tablas edocta, cruzadas por el
 * folio CENACE de la TBFin). El monto "sombra" se recalcula multiplicando los
 * MWh de la TBFin por el PML MDA del nodo del contrato (public.pml_mda) — si
 * CENACE hubiera usado otro nodo, el precio/monto no cuadraría.
 */

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const money = new Intl.NumberFormat("es-MX", {
  style: "currency", currency: "MXN", maximumFractionDigits: 2,
});
const nf3 = new Intl.NumberFormat("es-MX", { maximumFractionDigits: 3 });
const nf2 = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

function fmtFecha(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${String(d).padStart(2, "0")}-${MESES[m - 1].slice(0, 3).toLowerCase()}-${y}`;
}

const DIAS_SEMANA = [
  "Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado",
];

function diaSemana(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return DIAS_SEMANA[new Date(y, m - 1, d).getDay()];
}

const ESTADO_UI: Record<string, { cls: string; label: string }> = {
  ok: { cls: "bg-emerald-100 text-emerald-700", label: "OK" },
  difiere: { cls: "bg-red-100 text-red-700", label: "Difiere" },
  sin_edocta: { cls: "bg-slate-100 text-slate-500", label: "Sin edo. cuenta" },
};

type SortKey =
  | "contrato" | "folio" | "nodo" | "mwh"
  | "monto_bd" | "monto_cenace" | "delta" | "estado";

// Difiere primero al ordenar por estado (es lo que se quiere revisar).
const ESTADO_RANK: Record<string, number> = { difiere: 0, ok: 1, sin_edocta: 2 };

const deltaDe = (it: SombraCenaceItem): number | null =>
  it.monto_edocta != null ? it.monto_edocta - it.monto_mda_bd : null;

export function SombraCenacePage() {
  const hoy = new Date();
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [abierto, setAbierto] = useState<string | null>(null); // `${id}|${fecha}`
  // Días expandidos (nivel 1 → 2). Por default todos colapsados.
  const [diasAbiertos, setDiasAbiertos] = useState<Set<string>>(new Set());
  const toggleDia = (f: string) =>
    setDiasAbiertos((prev) => {
      const s = new Set(prev);
      if (s.has(f)) s.delete(f);
      else s.add(f);
      return s;
    });

  const [sortKey, setSortKey] = useState<SortKey>("contrato");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // Filtros por estado, COMBINABLES: cada KPI se prende/apaga; el primero
  // ("TBFins con folio") activa los tres a la vez.
  type Estado = "ok" | "difiere" | "sin_edocta";
  const TODOS: Estado[] = ["ok", "difiere", "sin_edocta"];
  const [filtros, setFiltros] = useState<Set<Estado>>(new Set(TODOS));
  const toggleFiltro = (e: Estado) =>
    setFiltros((prev) => {
      // Con los 3 activos ("sin filtro"), el clic individual deja SOLO ese estado.
      if (prev.size === 3) return new Set<Estado>([e]);
      const s = new Set(prev);
      if (s.has(e)) s.delete(e);
      else s.add(e);
      return s;
    });

  const q = useQuery({
    queryKey: ["sombra-cenace", anio, mes],
    queryFn: () => getSombraCenace(anio, mes),
    staleTime: 5 * 60 * 1000,
  });

  const data = q.data;

  const handleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      // Numéricos arrancan descendente (lo grande arriba); texto/fecha ascendente.
      setSortDir(["mwh", "monto_bd", "monto_cenace", "delta"].includes(k) ? "desc" : "asc");
    }
  };

  const items = useMemo(() => {
    const base = data?.items ?? [];
    // Set vacío = SIN filtro (se muestra todo), igual que con los 3 activos.
    const rows = filtros.size === 0 ? [...base] : base.filter((i) => filtros.has(i.estado as Estado));
    // null al final sin importar la dirección.
    const numCmp = (a: number | null, b: number | null) => {
      if (a == null && b == null) return 0;
      if (a == null) return sortDir === "asc" ? 1 : -1;
      if (b == null) return sortDir === "asc" ? -1 : 1;
      return a - b;
    };
    rows.sort((a, b) => {
      // Las filas se AGRUPAN por día: la fecha manda siempre; el sort de columnas
      // aplica dentro de cada día.
      const fcmp = a.fecha.localeCompare(b.fecha);
      if (fcmp !== 0) return fcmp;
      let cmp = 0;
      if (sortKey === "contrato") cmp = a.contrato.localeCompare(b.contrato);
      else if (sortKey === "folio") cmp = a.folio_cenace.localeCompare(b.folio_cenace);
      else if (sortKey === "nodo") cmp = a.nodo_contrato.localeCompare(b.nodo_contrato);
      else if (sortKey === "mwh") cmp = numCmp(a.mwh_etrm, b.mwh_etrm);
      else if (sortKey === "monto_bd") cmp = numCmp(a.monto_mda_bd, b.monto_mda_bd);
      else if (sortKey === "monto_cenace") cmp = numCmp(a.monto_edocta, b.monto_edocta);
      else if (sortKey === "delta") {
        const da = deltaDe(a), db = deltaDe(b);
        cmp = numCmp(da == null ? null : Math.abs(da), db == null ? null : Math.abs(db));
      } else if (sortKey === "estado") {
        cmp = (ESTADO_RANK[a.estado] ?? 9) - (ESTADO_RANK[b.estado] ?? 9);
      }
      if (cmp === 0) cmp = a.contrato.localeCompare(b.contrato);
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir, filtros]);

  // Grupos por día, en orden: [fecha, filas del día]
  const grupos = useMemo(() => {
    const out: { fecha: string; filas: SombraCenaceItem[] }[] = [];
    for (const it of items) {
      const g = out[out.length - 1];
      if (g && g.fecha === it.fecha) g.filas.push(it);
      else out.push({ fecha: it.fecha, filas: [it] });
    }
    return out;
  }, [items]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Sombra CENACE</h1>
          <p className="text-sm text-slate-500 mt-1">
            TBFin enviadas vs estado de cuenta CENACE. El monto sombra = MWh de la TBFin ×
            PML MDA del nodo del contrato — valida que CENACE liquidó con el nodo correcto.
            Las TBFin <b>automáticas</b> (p. ej. Atria) no se envían: ahí se compara el
            modelo del motor TBFin contra lo liquidado.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={mes} onChange={(e) => setMes(Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm bg-white">
            {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <select value={anio} onChange={(e) => setAnio(Number(e.target.value))}
            className="border rounded px-2 py-1.5 text-sm bg-white">
            {Array.from({ length: 4 }, (_, i) => hoy.getFullYear() - i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {data && (
        <div className="flex flex-wrap items-center gap-3">
          <Kpi valor={data.totales.tbfins} texto="TBFins con folio" cls="text-slate-800"
            activo={filtros.size === 3 || filtros.size === 0}
            onClick={() => setFiltros(new Set(TODOS))} />
          <Kpi valor={data.totales.ok} texto="Cuadran (OK)" cls="text-emerald-700" icon={<Check className="w-4 h-4" />}
            activo={filtros.has("ok")} onClick={() => toggleFiltro("ok")} />
          <Kpi valor={data.totales.difieren} texto="Difieren" cls="text-red-700" icon={<AlertTriangle className="w-4 h-4" />}
            activo={filtros.has("difiere")} onClick={() => toggleFiltro("difiere")} />
          <Kpi valor={data.totales.sin_edocta} texto="Sin edo. de cuenta" cls="text-slate-500" icon={<Clock className="w-4 h-4" />}
            activo={filtros.has("sin_edocta")} onClick={() => toggleFiltro("sin_edocta")} />
          <span className="text-xs text-slate-400 ml-auto">
            Estado de cuenta disponible hasta:{" "}
            <b className="text-slate-600">{data.edocta_hasta ? fmtFecha(data.edocta_hasta) : "—"}</b>
            {" "}(CENACE publica con ~7 días de rezago)
          </span>
        </div>
      )}

      {q.isLoading && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">Cargando…</div>
      )}
      {q.isError && (
        <div className="bg-white border rounded-lg p-8 text-center text-red-600 text-sm">
          {(q.error as Error).message}
        </div>
      )}
      {data && items.length === 0 && (
        <div className="bg-white border rounded-lg p-8 text-center text-slate-500 text-sm">
          {filtros.size > 0 && filtros.size < 3
            ? <>Sin TBFins con los estados seleccionados en {MESES[mes - 1]} {anio} — clic en "TBFins con folio" para ver todo.</>
            : <>Sin TBFins con folio CENACE en {MESES[mes - 1]} {anio}.</>}
        </div>
      )}

      {items.length > 0 && (
        <div className="bg-white border rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-slate-600 text-left">
              <tr>
                <th className="px-2 py-2 w-6" />
                <SortableTh col="contrato" align="left" current={sortKey} dir={sortDir} onSort={handleSort}>Contrato</SortableTh>
                <SortableTh col="folio" align="left" current={sortKey} dir={sortDir} onSort={handleSort}>Folio CENACE</SortableTh>
                <SortableTh col="nodo" align="left" current={sortKey} dir={sortDir} onSort={handleSort}>Nodo</SortableTh>
                <SortableTh col="mwh" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>MWh (TBFin / CENACE)</SortableTh>
                <SortableTh col="monto_bd" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>Monto sombra (MDA)</SortableTh>
                <SortableTh col="monto_cenace" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>Monto CENACE</SortableTh>
                <SortableTh col="delta" align="right" current={sortKey} dir={sortDir} onSort={handleSort}>Δ</SortableTh>
                <SortableTh col="estado" align="center" current={sortKey} dir={sortDir} onSort={handleSort}>Estado</SortableTh>
              </tr>
            </thead>
            <tbody>
              {grupos.map((g) => {
                const sumMwh = g.filas.reduce((s, x) => s + x.mwh_etrm, 0);
                const conMwhEd = g.filas.filter((x) => x.mwh_edocta != null);
                const sumMwhEd = conMwhEd.length
                  ? conMwhEd.reduce((s, x) => s + (x.mwh_edocta ?? 0), 0) : null;
                const sumBd = g.filas.reduce((s, x) => s + x.monto_mda_bd, 0);
                const conEd = g.filas.filter((x) => x.monto_edocta != null);
                const sumEd = conEd.reduce((s, x) => s + (x.monto_edocta ?? 0), 0);
                // Semáforo del día: difiere si alguna difiere; luego sin_edocta; si no, ok.
                const estadoDia = g.filas.some((x) => x.estado === "difiere")
                  ? "difiere"
                  : g.filas.some((x) => x.estado === "sin_edocta") ? "sin_edocta" : "ok";
                const openDia = diasAbiertos.has(g.fecha);
                return (
                  <FragmentoDia key={g.fecha} fecha={g.fecha} n={g.filas.length}
                    nNodos={new Set(g.filas.map((x) => x.nodo_contrato)).size}
                    sumMwh={sumMwh} sumMwhEd={sumMwhEd} sumBd={sumBd}
                    sumEd={conEd.length ? sumEd : null}
                    estado={estadoDia} open={openDia} onToggle={() => toggleDia(g.fecha)}>
                    {/* Nivel 2: subgrupos Emitidas (MPP vendedor) / Recibidas (comprador),
                        cada uno con su subtotal; el sort de columnas aplica dentro. */}
                    {openDia && (
                      [
                        { rol: "vendedor", label: "Emitidas" },
                        { rol: "comprador", label: "Recibidas" },
                      ] as const
                    ).map(({ rol, label }) => {
                      const filas = g.filas.filter((x) =>
                        rol === "vendedor" ? x.rol_mpp === "vendedor" : x.rol_mpp !== "vendedor",
                      );
                      if (filas.length === 0) return null;
                      return (
                        <SubgrupoRol key={`${g.fecha}|${rol}`} label={label} filas={filas}>
                          {filas.map((it) => {
                            const key = `${it.id_contrato}|${it.fecha}`;
                            const open = abierto === key;
                            const delta = it.monto_edocta != null ? it.monto_edocta - it.monto_mda_bd : null;
                            const ui = ESTADO_UI[it.estado];
                            return (
                              <FilaConDetalle key={key} it={it} open={open} delta={delta} ui={ui}
                                onToggle={() => setAbierto(open ? null : key)} />
                            );
                          })}
                        </SubgrupoRol>
                      );
                    })}
                  </FragmentoDia>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SortableTh({ col, align, current, dir, onSort, children }: {
  col: SortKey;
  align: "left" | "right" | "center";
  current: SortKey;
  dir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  children: React.ReactNode;
}) {
  const isActive = current === col;
  const Icon = !isActive ? ArrowUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      className={`px-3 py-2 font-medium select-none cursor-pointer hover:bg-slate-200 ${
        align === "right" ? "text-right" : align === "center" ? "text-center" : ""
      }`}
      onClick={() => onSort(col)}
    >
      <span className={`inline-flex items-center gap-1 ${align === "right" ? "justify-end" : ""}`}>
        {children}
        <Icon className={`w-3 h-3 shrink-0 ${isActive ? "" : "opacity-40"}`} aria-hidden="true" />
      </span>
    </th>
  );
}

function Kpi({ valor, texto, cls, icon, activo, onClick }: {
  valor: number; texto: string; cls: string; icon?: React.ReactNode;
  activo?: boolean; onClick?: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={activo ? "Filtro activo — clic para quitar" : "Filtrar la tabla por este estado"}
      className={`bg-white border rounded-lg px-4 py-2 flex items-center gap-2 transition
        hover:bg-slate-50 ${activo ? "ring-2 ring-blue-500 border-blue-400" : ""}`}
    >
      {icon && <span className={cls}>{icon}</span>}
      <span className={`text-lg font-semibold tabular-nums ${cls}`}>{valor}</span>
      <span className="text-xs text-slate-500">{texto}</span>
    </button>
  );
}

function FragmentoDia({ fecha, n, nNodos, sumMwh, sumMwhEd, sumBd, sumEd, estado, open, onToggle, children }: {
  fecha: string; n: number; nNodos: number; sumMwh: number; sumMwhEd: number | null;
  sumBd: number; sumEd: number | null;
  estado: "ok" | "difiere" | "sin_edocta"; open: boolean; onToggle: () => void;
  children: React.ReactNode;
}) {
  const ui = ESTADO_UI[estado];
  const delta = sumEd != null ? sumEd - sumBd : null;
  return (
    <>
      <tr
        className={`border-t cursor-pointer select-none hover:bg-slate-100 ${
          open ? "bg-slate-100" : "bg-slate-50"
        } ${estado === "difiere" ? "bg-red-50/60 hover:bg-red-50" : ""}`}
        onClick={onToggle}
      >
        {/* Alineado a las columnas de la tabla: fecha (chevron+Contrato),
            # TBFins en Folio, nodos distintos en Nodo, y MWh/Sombra/CENACE/Δ/Estado
            en sus propias columnas. */}
        <td colSpan={2} className="px-2 py-2">
          <span className="inline-flex items-center gap-2">
            {open
              ? <ChevronDown className="w-4 h-4 text-slate-400" />
              : <ChevronRight className="w-4 h-4 text-slate-400" />}
            <span className="font-semibold text-slate-700">
              {diaSemana(fecha)} {fmtFecha(fecha)}
            </span>
          </span>
        </td>
        <td className="px-3 py-2 text-xs text-slate-500">
          {n} TBFin{n === 1 ? "" : "s"}
        </td>
        <td className="px-3 py-2 text-xs text-slate-500">
          {nNodos} nodo{nNodos === 1 ? "" : "s"}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700">
          {nf3.format(sumMwh)}
          <span className="text-slate-400 font-normal">
            {" "}/ {sumMwhEd != null ? nf3.format(sumMwhEd) : "—"}
          </span>
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700">
          {money.format(sumBd)}
        </td>
        <td className="px-3 py-2 text-right tabular-nums font-medium text-slate-700">
          {sumEd != null ? money.format(sumEd) : "—"}
        </td>
        <td className={`px-3 py-2 text-right tabular-nums font-medium ${
          delta != null && Math.abs(delta) > 0.5 ? "text-red-600" : "text-slate-400"
        }`}>
          {delta != null ? money.format(delta) : "—"}
        </td>
        <td className="px-3 py-2 text-center">
          <span className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${ui.cls}`}>
            {ui.label}
          </span>
        </td>
      </tr>
      {children}
    </>
  );
}

// Subgrupo del día por rol de MPP: Emitidas (vendedor) / Recibidas (comprador),
// con subtotales alineados a las columnas de la tabla.
function SubgrupoRol({ label, filas, children }: {
  label: string; filas: SombraCenaceItem[]; children: React.ReactNode;
}) {
  const sumMwh = filas.reduce((s, x) => s + x.mwh_etrm, 0);
  const conMwhEd = filas.filter((x) => x.mwh_edocta != null);
  const sumMwhEd = conMwhEd.length
    ? conMwhEd.reduce((s, x) => s + (x.mwh_edocta ?? 0), 0) : null;
  const sumBd = filas.reduce((s, x) => s + x.monto_mda_bd, 0);
  const conEd = filas.filter((x) => x.monto_edocta != null);
  const sumEd = conEd.length ? conEd.reduce((s, x) => s + (x.monto_edocta ?? 0), 0) : null;
  const delta = sumEd != null ? sumEd - sumBd : null;
  const estado = filas.some((x) => x.estado === "difiere")
    ? "difiere"
    : filas.some((x) => x.estado === "sin_edocta") ? "sin_edocta" : "ok";
  const ui = ESTADO_UI[estado];
  return (
    <>
      <tr className="border-t bg-slate-50/70">
        <td />
        <td className="px-3 py-1.5 pl-6 text-xs font-semibold text-slate-500 uppercase tracking-wide">
          {label}
        </td>
        <td className="px-3 py-1.5 text-xs text-slate-400">
          {filas.length} TBFin{filas.length === 1 ? "" : "s"}
        </td>
        <td />
        <td className="px-3 py-1.5 text-right tabular-nums text-xs text-slate-500">
          {nf3.format(sumMwh)}
          <span className="text-slate-400"> / {sumMwhEd != null ? nf3.format(sumMwhEd) : "—"}</span>
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-xs text-slate-500">
          {money.format(sumBd)}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums text-xs text-slate-500">
          {sumEd != null ? money.format(sumEd) : "—"}
        </td>
        <td className={`px-3 py-1.5 text-right tabular-nums text-xs ${
          delta != null && Math.abs(delta) > 0.5 ? "text-red-600" : "text-slate-400"
        }`}>
          {delta != null ? money.format(delta) : "—"}
        </td>
        <td className="px-3 py-1.5 text-center">
          <span className={`inline-block text-[10px] font-medium rounded-full px-1.5 py-0.5 ${ui.cls}`}>
            {ui.label}
          </span>
        </td>
      </tr>
      {children}
    </>
  );
}

function FilaConDetalle({ it, open, delta, ui, onToggle }: {
  it: SombraCenaceItem;
  open: boolean;
  delta: number | null;
  ui: { cls: string; label: string };
  onToggle: () => void;
}) {
  const detQ = useQuery({
    queryKey: ["sombra-cenace-detalle", it.id_contrato, it.fecha],
    queryFn: () => getSombraCenaceDetalle(it.id_contrato, it.fecha),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  return (
    <>
      <tr className={`border-t hover:bg-slate-50 cursor-pointer ${it.estado === "difiere" ? "bg-red-50/40" : ""}`}
        onClick={onToggle}>
        <td className="px-2 py-1.5 text-slate-400">
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </td>
        <td className="px-3 py-1.5 font-medium text-slate-800">{it.contrato}</td>
        <td className="px-3 py-1.5 font-mono text-xs">{it.folio_cenace}
          {it.estatus_cenace && <span className="ml-1.5 text-slate-400">({it.estatus_cenace})</span>}
          {it.automatica && (
            <span
              className="ml-1.5 inline-block text-[10px] font-sans font-medium rounded-full px-1.5 py-0.5 bg-violet-100 text-violet-700 align-middle"
              title="TBFin automática: MPP no la envía — se compara el MODELO del motor TBFin vs lo liquidado por CENACE (el folio sale del propio estado de cuenta)"
            >
              automática
            </span>
          )}
          {it.nota && (
            <span className="ml-1 text-amber-600" title={it.nota}>⚠</span>
          )}
        </td>
        <td className="px-3 py-1.5 font-mono text-xs">
          {it.nodo_contrato}
          {it.encontrado && it.nodo_ok === false && (
            <span className="ml-1 inline-flex items-center text-red-600" title={`CENACE usó: ${it.nodo_edocta}`}>
              <X className="w-3.5 h-3.5" /> {it.nodo_edocta}
            </span>
          )}
          {it.encontrado && it.nodo_ok && (
            <Check className="w-3.5 h-3.5 inline ml-1 text-emerald-600" />
          )}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {nf3.format(it.mwh_etrm)}
          <span className="text-slate-400"> / {it.mwh_edocta != null ? nf3.format(it.mwh_edocta) : "—"}</span>
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {money.format(it.monto_mda_bd)}
          {it.pml_incompleto && (
            <span className="text-amber-600 ml-1" title="PML MDA incompleto en BD para este día">⚠</span>
          )}
        </td>
        <td className="px-3 py-1.5 text-right tabular-nums">
          {it.monto_edocta != null ? money.format(it.monto_edocta) : "—"}
        </td>
        <td className={`px-3 py-1.5 text-right tabular-nums ${delta != null && Math.abs(delta) > 0.5 ? "text-red-600 font-medium" : "text-slate-400"}`}>
          {delta != null ? money.format(delta) : "—"}
        </td>
        <td className="px-3 py-1.5 text-center">
          <span className={`inline-block text-[11px] font-medium rounded-full px-2 py-0.5 ${ui.cls}`}>
            {ui.label}
          </span>
        </td>
      </tr>
      {open && (
        <tr className="border-t bg-slate-50/60">
          <td colSpan={9} className="px-6 py-3">
            {detQ.isLoading && <div className="text-xs text-slate-500 py-2">Cargando detalle horario…</div>}
            {detQ.isError && <div className="text-xs text-red-600 py-2">{(detQ.error as Error).message}</div>}
            {detQ.data && (
              <table className="text-xs w-full max-w-4xl">
                <thead className="text-slate-500">
                  <tr className="border-b">
                    <th className="px-2 py-1 text-right">Hora</th>
                    <th className="px-2 py-1 text-right">MWh TBFin</th>
                    <th className="px-2 py-1 text-right">PML MDA (BD)</th>
                    <th className="px-2 py-1 text-right">Monto esperado</th>
                    <th className="px-2 py-1 text-right">MWh CENACE</th>
                    <th className="px-2 py-1 text-right">Precio CENACE</th>
                    <th className="px-2 py-1 text-right">Monto CENACE</th>
                    <th className="px-2 py-1 text-left">Nodo CENACE</th>
                    <th className="px-2 py-1 text-center">✓</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {detQ.data.horas.filter((h) => h.mwh_etrm > 0 || (h.mwh_edocta ?? 0) > 0).map((h) => (
                    <tr key={h.hora} className={`border-b last:border-0 ${h.ok === false ? "bg-red-50" : ""}`}>
                      <td className="px-2 py-1 text-right">{h.hora}</td>
                      <td className="px-2 py-1 text-right">{nf3.format(h.mwh_etrm)}</td>
                      <td className="px-2 py-1 text-right">{h.pml_bd != null ? nf2.format(h.pml_bd) : "—"}</td>
                      <td className="px-2 py-1 text-right">{h.monto_esperado != null ? nf2.format(h.monto_esperado) : "—"}</td>
                      <td className="px-2 py-1 text-right">{h.mwh_edocta != null ? nf3.format(h.mwh_edocta) : "—"}</td>
                      <td className="px-2 py-1 text-right">{h.precio_edocta != null ? nf2.format(h.precio_edocta) : "—"}</td>
                      <td className="px-2 py-1 text-right">{h.monto_edocta != null ? nf2.format(h.monto_edocta) : "—"}</td>
                      <td className="px-2 py-1 font-mono">{h.nodo_edocta ?? "—"}</td>
                      <td className="px-2 py-1 text-center">
                        {h.ok == null ? <span className="text-slate-300">—</span>
                          : h.ok ? <Check className="w-3.5 h-3.5 inline text-emerald-600" />
                            : <X className="w-3.5 h-3.5 inline text-red-600" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
